import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sun, Cloud, CloudSun, CloudFog, CloudRain, CloudSnow, Database, HelpCircle, RefreshCw, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { authFetch } from '../../lib/auth';
import { AnimatedNumber } from '../../os3/theme';

/* Kitxens AI OS v2 — shared UI primitives (light executive theme). */

export const ORANGE = '#F77F11';

export const STAGE_GRADIENTS = [
  'linear-gradient(135deg,#475569 0%,#64748B 100%)',
  'linear-gradient(135deg,#0369A1 0%,#38BDF8 100%)',
  'linear-gradient(135deg,#0E7490 0%,#22D3EE 100%)',
  'linear-gradient(135deg,#C2570A 0%,#F77F11 100%)',
  'linear-gradient(135deg,#B45309 0%,#FBBF24 100%)',
  'linear-gradient(135deg,#6D28D9 0%,#A78BFA 100%)',
  'linear-gradient(135deg,#4338CA 0%,#818CF8 100%)',
  'linear-gradient(135deg,#047857 0%,#34D399 100%)',
];

export function useOs2(path) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    let p = path;
    if (/^\/(sales|customers|pipeline|overview|marketing|content|finance|affiliates)/.test(path)) {
      const days = localStorage.getItem('kx_os2_days') || '';
      const q = [];
      if (days) q.push(days === 'yd' ? 'days=1&offset=1' : `days=${days}`);
      if (localStorage.getItem('kx_os2_test') === '1') q.push('include_test=true');
      if (q.length) p += (p.includes('?') ? '&' : '?') + q.join('&');
    }
    setLoading(true);
    return authFetch(`/os2${p}`).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    }).then((j) => { setData(j); setErr(null); setFetchedAt(new Date()); })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const onRange = () => load();
    window.addEventListener('kx-os2-range', onRange);
    return () => window.removeEventListener('kx-os2-range', onRange);
    /* eslint-disable-next-line */
  }, [path]);
  return { data, err, reload: load, fetchedAt, loading };
}

export function useRangeLabel(isEs) {
  const [days, setDays] = useState(localStorage.getItem('kx_os2_days') || '');
  useEffect(() => {
    const on = () => setDays(localStorage.getItem('kx_os2_days') || '');
    window.addEventListener('kx-os2-range', on);
    return () => window.removeEventListener('kx-os2-range', on);
  }, []);
  const r = RANGES.find((x) => x[0] === days) || RANGES[0];
  return { days, label: isEs ? r[2] : r[1] };
}

export function RangeNotice({ isEs, windows }) {
  /* One honest line on views that do NOT follow the global period selector. */
  const { days, label } = useRangeLabel(isEs);
  if (!days) return null;
  return (
    <p className="flex items-center gap-1.5 text-[11px] text-gray-400" data-testid="range-notice">
      <Info className="w-3 h-3 shrink-0 text-gray-300" />
      {isEs
        ? `El selector de periodo (${label}) no aplica en esta vista — usa sus propias ventanas: ${windows}.`
        : `The period selector (${label}) does not apply to this view — it uses its own windows: ${windows}.`}
    </p>
  );
}

export function ScopeTag({ current, label, isEs }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide ${current ? 'bg-sky-50 text-sky-600 border-sky-200' : 'bg-orange-50 text-[#d8651a] border-orange-200'}`}>
      {current ? (isEs ? 'Actual' : 'Current') : label}
    </span>
  );
}

export const RANGES = [
  ['', 'Lifetime', 'Histórico'],
  ['1', 'Today', 'Hoy'],
  ['yd', 'Yesterday', 'Ayer'],
  ['7', 'Last 7 days', 'Últimos 7 días'],
  ['30', 'Last 30 days', 'Últimos 30 días'],
  ['90', 'Last 90 days', 'Últimos 90 días'],
  ['365', 'Last 12 months', 'Últimos 12 meses'],
];

export function Pager({ page, pages, setPage, testid = 'pager' }) {
  if (pages <= 1) return null;
  const nums = [];
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  for (let i = start; i <= Math.min(pages, start + 4); i += 1) nums.push(i);
  return (
    <div className="flex items-center justify-center gap-1.5 mt-4" data-testid={testid}>
      <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#F77F11] disabled:opacity-30" data-testid={`${testid}-prev`}>
        <ChevronLeft className="w-4 h-4" />
      </button>
      {nums[0] > 1 && <span className="text-gray-300 text-[12px] px-1">…</span>}
      {nums.map((n) => (
        <button key={n} onClick={() => setPage(n)} data-testid={`${testid}-page-${n}`}
          className={`w-8 h-8 rounded-lg text-[12.5px] font-bold ${n === page ? 'bg-[#F77F11] text-white' : 'border border-gray-200 text-gray-600 hover:border-[#F77F11]'}`}>
          {n}
        </button>
      ))}
      {nums[nums.length - 1] < pages && <span className="text-gray-300 text-[12px] px-1">…</span>}
      <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#F77F11] disabled:opacity-30" data-testid={`${testid}-next`}>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export function daysAgoLabel(iso, isEs) {
  if (!iso) return '';
  const dte = new Date(iso);
  const days = Math.floor((Date.now() - dte.getTime()) / 86400000);
  const rel = days <= 0 ? (isEs ? 'hoy' : 'today') : days === 1 ? (isEs ? 'ayer' : 'yesterday') : (isEs ? `hace ${days} d` : `${days}d ago`);
  return `${rel} · ${dte.toLocaleDateString(isEs ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' })}`;
}

const CLASS_STYLE = {
  production: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  test: 'bg-gray-100 text-gray-500 border-gray-200',
  affiliate: 'bg-sky-50 text-sky-700 border-sky-200',
  index_ai: 'bg-[#FFF7ED] text-[#b45309] border-orange-200',
  newsletter: 'bg-violet-50 text-violet-700 border-violet-200',
};

export function ClassBadge({ c }) {
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide border ${CLASS_STYLE[c] || CLASS_STYLE.test}`}>{(c || '').replace('_', ' ')}</span>;
}

export const toCSV = (rows, cols) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.map(esc).join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `kitxens-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

export const wIcon = (code, cls = 'w-4 h-4') => {
  if (code === 0) return <Sun className={`${cls} text-amber-500`} />;
  if (code <= 2) return <CloudSun className={`${cls} text-amber-400`} />;
  if (code === 3) return <Cloud className={`${cls} text-gray-400`} />;
  if (code <= 48) return <CloudFog className={`${cls} text-gray-400`} />;
  if (code <= 67 || (code >= 80 && code <= 82)) return <CloudRain className={`${cls} text-sky-500`} />;
  if (code <= 77 || (code >= 85 && code <= 86)) return <CloudSnow className={`${cls} text-sky-300`} />;
  return <CloudRain className={`${cls} text-sky-500`} />;
};

export const money = (v, currency = 'USD') =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);

export const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }); } catch { return '—'; }
};

export const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
};

/* ---------- Help system: every business concept documented ---------- */

export const HELP = {
  en: {
    revenue: 'Total money collected through Stripe since launch. Below: revenue of the last 30 days.',
    mrr: 'Monthly Recurring Revenue — the sum of what active subscriptions pay per month (payments seen in the last 35 days). The most important SaaS growth metric.',
    arr: 'Annual Recurring Revenue — MRR × 12. What the business would bill in a year at the current pace.',
    leads_today: 'People who left their contact info TODAY — via a form or the free Index AI scan. Fresh leads convert best when contacted within hours.',
    meetings: 'Discovery calls booked with prospects (CRM stage "Call booked"). Each one is a real sales conversation.',
    customers: 'Contacts who have paid at least once or reached the "Customer" stage in the CRM.',
    opportunities: 'Qualified leads + booked calls: the deals you could realistically close right now.',
    pipeline_value: 'The estimated $ value of all open deals. Requires assigning a deal value per opportunity — not tracked yet.',
    health: 'A 0–100 score computed only from real signals: AI agents active, automations without errors, native Event Bus delivery and Brevo webhook status. Below 60 = act today.',
    lead: 'A person who showed interest and left contact data (email). Not yet verified as a real buyer.',
    stage: 'Where the person is in the sales journey: New lead → Qualified → Call booked → Customer. It updates automatically from real activity.',
    probability: 'Estimated chance of closing, based on the stage: New 10% · Qualified 35% · Call booked 60% · Customer 100%.',
    lead_score: 'Engagement points earned from real actions: completing a scan, opening emails, clicking links. Higher = warmer.',
    index_ai_score: '0–100 result of the Index AI scan of their website: how visible the restaurant is on Google and AI search (ChatGPT). Low score = strong sales angle.',
    scan: 'Completed the free Index AI website scan — our door-opener product.',
    report: 'Unlocked/downloaded the full audit report (left their email to get it).',
    email_sent: 'We sent them at least one email through Brevo.',
    email_opened: 'They opened one of our emails (tracked by the Brevo webhook).',
    email_clicked: 'They clicked a link inside one of our emails — high interest signal.',
    source: 'Where the contact came from: web form, Index AI scan, affiliate link, demo…',
    affiliate: 'The partner whose link referred this contact. Attribution lasts 90 days and pays 30% recurring.',
    campaign: 'The marketing campaign tag (UTM) carried by the link they clicked.',
    agent: 'The AI teammate responsible for this contact (Eva coordinates by default).',
    next_action: 'What the system recommends doing next with this contact, based on their stage and behavior.',
    last_activity: 'The most recent thing this contact did or that happened to them (scan, email, payment…).',
    conversion: 'Real conversion rates between stages, computed from your CRM — no estimates.',
    journey: 'The milestones this person completed: Scan → Report → Email sent → Opened → Clicked. More green = closer to buying.',
    open_rate: 'Percentage of delivered emails that were opened (30-day transactional, real Brevo data).',
    click_rate: 'Percentage of delivered emails where a link was clicked. The strongest email engagement signal.',
    payouts: 'Commissions owed to partners: pending (awaiting review) + approved (ready to pay). Paid shown below.',
    referrals: 'People who arrived through a partner link and left their contact info.',
    page_views: 'Pages viewed on kitxens.com, measured by our own first-party beacon (no cookies needed).',
    read_time: 'Average time visitors actually spend reading articles — real engagement, not just clicks.',
    whatsapp_clicks: 'Visitors who clicked to open a WhatsApp conversation with Rachel.',
    intents: 'What Rachel detected each visitor was looking for (pricing, demo, partner…), classified automatically.',
  },
  es: {
    revenue: 'Dinero total cobrado por Stripe desde el lanzamiento. Debajo: ingresos de los últimos 30 días.',
    mrr: 'Ingresos Recurrentes Mensuales — la suma de lo que pagan al mes las suscripciones activas (pagos vistos en los últimos 35 días). La métrica de crecimiento SaaS más importante.',
    arr: 'Ingresos Recurrentes Anuales — MRR × 12. Lo que facturaría el negocio en un año al ritmo actual.',
    leads_today: 'Personas que dejaron sus datos HOY — vía formulario o el scan gratuito de Index AI. Los leads frescos convierten mejor si los contactas en horas.',
    meetings: 'Llamadas de descubrimiento agendadas con prospectos (etapa CRM "Llamada agendada"). Cada una es una conversación de venta real.',
    customers: 'Contactos que han pagado al menos una vez o llegaron a la etapa "Cliente" en el CRM.',
    opportunities: 'Leads calificados + llamadas agendadas: los tratos que podrías cerrar de forma realista ahora mismo.',
    pipeline_value: 'El valor estimado en $ de todos los tratos abiertos. Requiere asignar un valor a cada oportunidad — aún no se registra.',
    health: 'Puntuación 0–100 calculada solo con señales reales: agentes AI activos, automatizaciones sin errores, entrega de eventos al Event Bus nativo y estado del webhook de Brevo. Menos de 60 = actúa hoy.',
    lead: 'Una persona que mostró interés y dejó sus datos de contacto (email). Aún no verificada como comprador real.',
    stage: 'Dónde está la persona en el recorrido de venta: Lead nuevo → Calificado → Llamada agendada → Cliente. Se actualiza automáticamente con actividad real.',
    probability: 'Probabilidad estimada de cierre según la etapa: Nuevo 10% · Calificado 35% · Llamada 60% · Cliente 100%.',
    lead_score: 'Puntos de engagement ganados con acciones reales: completar un scan, abrir emails, hacer clic. Más alto = más caliente.',
    index_ai_score: 'Resultado 0–100 del scan de Index AI a su web: qué tan visible es el restaurante en Google y en búsqueda IA (ChatGPT). Score bajo = gran argumento de venta.',
    scan: 'Completó el scan gratuito de Index AI a su web — nuestro producto de entrada.',
    report: 'Desbloqueó/descargó el reporte completo de auditoría (dejó su email para obtenerlo).',
    email_sent: 'Le enviamos al menos un email por Brevo.',
    email_opened: 'Abrió alguno de nuestros emails (rastreado por el webhook de Brevo).',
    email_clicked: 'Hizo clic en un enlace de nuestros emails — señal de alto interés.',
    source: 'De dónde llegó el contacto: formulario web, scan de Index AI, enlace de afiliado, demo…',
    affiliate: 'El partner cuyo enlace refirió a este contacto. La atribución dura 90 días y paga 30% recurrente.',
    campaign: 'La etiqueta de campaña (UTM) que traía el enlace en el que hizo clic.',
    agent: 'El agente AI responsable de este contacto (Eva coordina por defecto).',
    next_action: 'Lo que el sistema recomienda hacer a continuación con este contacto, según su etapa y comportamiento.',
    last_activity: 'Lo más reciente que hizo este contacto o le ocurrió (scan, email, pago…).',
    conversion: 'Tasas de conversión reales entre etapas, calculadas desde tu CRM — sin estimaciones.',
    journey: 'Los hitos que completó esta persona: Scan → Reporte → Email enviado → Abierto → Click. Más verde = más cerca de comprar.',
    open_rate: 'Porcentaje de emails entregados que fueron abiertos (transaccional 30 días, datos reales de Brevo).',
    click_rate: 'Porcentaje de emails entregados donde hicieron clic en un enlace. La señal de engagement más fuerte.',
    payouts: 'Comisiones debidas a partners: pendientes (en revisión) + aprobadas (listas para pagar). Pagadas se muestra debajo.',
    referrals: 'Personas que llegaron por el enlace de un partner y dejaron sus datos.',
    page_views: 'Páginas vistas en kitxens.com, medidas por nuestro propio beacon first-party (sin cookies).',
    read_time: 'Tiempo medio que los visitantes pasan realmente leyendo artículos — engagement real, no solo clics.',
    whatsapp_clicks: 'Visitantes que hicieron clic para abrir una conversación de WhatsApp con Rachel.',
    intents: 'Qué buscaba cada visitante según Rachel (precios, demo, partner…), clasificado automáticamente.',
  },
};

export const helpText = (key, isEs) => HELP[isEs ? 'es' : 'en'][key] || '';

export function InfoTip({ text, k, isEs }) {
  const t = text || helpText(k, isEs);
  if (!t) return null;
  return (
    <span className="relative inline-flex group/tip align-middle ml-1">
      <button type="button" aria-label={t} data-testid={k ? `help-${k}` : undefined}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        className="text-gray-300 hover:text-[#F77F11] focus:text-[#F77F11] focus:outline-none transition-colors cursor-help">
        <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      <span role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 rounded-lg bg-gray-900 text-white text-[11.5px] leading-relaxed px-3 py-2 opacity-0 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100 transition-opacity duration-150 z-[80] shadow-xl normal-case font-normal tracking-normal text-left whitespace-normal">
        {t}
      </span>
    </span>
  );
}

export const Fade = ({ children, delay = 0, className = '', ...rest }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: 'easeOut' }} className={className} {...rest}>
    {children}
  </motion.div>
);

export const SEVERITY = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  info: 'bg-sky-50 border-sky-200 text-sky-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-700',
  error: 'bg-red-50 border-red-200 text-red-700',
};

export const timeAgo = (iso, isEs) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}min`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

export function Freshness({ at, onReload, loading, isEs }) {
  if (!at) return null;
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11.5px] text-gray-500" data-testid="os2-freshness">
      <span className="relative flex w-1.5 h-1.5" aria-hidden="true">
        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
        <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
      </span>
      {isEs ? 'En vivo · actualizado' : 'Live · updated'} <span className="tabular-nums font-semibold text-gray-700">{at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      {onReload && (
        <button onClick={onReload} title={isEs ? 'Refrescar datos ahora' : 'Refresh data now'} aria-label={isEs ? 'Refrescar' : 'Refresh'}
          data-testid="os2-refresh-btn"
          className="p-0.5 rounded text-gray-400 hover:text-[#F77F11] transition-colors">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      )}
    </span>
  );
}

let _moduleCache = null;
export function useModuleStatus(module) {
  const [st, setSt] = useState(_moduleCache ? _moduleCache[module] : null);
  useEffect(() => {
    if (!module) return;
    if (_moduleCache) { setSt(_moduleCache[module]); return; }
    authFetch('/os2/module-status').then((r) => r.json()).then((j) => {
      _moduleCache = Object.fromEntries((j.modules || []).map((m) => [m.module, m]));
      setSt(_moduleCache[module]);
    }).catch(() => {});
  }, [module]);
  return st;
}

const VALIDATION_STYLE = {
  validated: ['bg-emerald-50 text-emerald-700 border-emerald-200', '✓'],
  partial: ['bg-amber-50 text-amber-700 border-amber-200', '◐'],
  pending: ['bg-amber-50 text-amber-700 border-amber-200', '…'],
  blocked: ['bg-red-50 text-red-600 border-red-200', '⛔'],
  failed: ['bg-red-50 text-red-600 border-red-200', '✕'],
};
const VALIDATION_LABEL = {
  validated: ['Validated', 'Validado'],
  partial: ['Partially validated', 'Validación parcial'],
  pending: ['Pending validation', 'Validación pendiente'],
  blocked: ['Blocked', 'Bloqueado'],
  failed: ['Failed', 'Fallido'],
};

export function ValidationChip({ status, isEs, testid }) {
  if (!status) return null;
  const [cls, icon] = VALIDATION_STYLE[status.validated] || VALIDATION_STYLE.pending;
  const label = (VALIDATION_LABEL[status.validated] || VALIDATION_LABEL.pending)[isEs ? 1 : 0];
  return (
    <span title={status.note || ''} data-testid={testid || 'os2-validation-chip'}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide cursor-default ${cls}`}>
      {icon} {label}
    </span>
  );
}

export function PageHead({ title, question, right, fresh, module }) {
  const modStatus = useModuleStatus(module);
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl lg:text-3xl tracking-tight font-semibold text-gray-900" data-testid="os2-page-title">{title}</h1>
        <p className="mt-1.5 text-sm text-gray-500">{question}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {module && <ValidationChip status={modStatus} isEs={fresh?.isEs} />}
        {fresh && <Freshness {...fresh} />}
        {right}
      </div>
    </div>
  );
}

export function Card({ children, className = '', ...rest }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function LiveDot({ color = 'emerald', className = '' }) {
  const solid = { emerald: 'bg-emerald-500', orange: 'bg-[#F77F11]', red: 'bg-red-500', gray: 'bg-gray-300' }[color] || 'bg-emerald-500';
  const ping = { emerald: 'bg-emerald-400', orange: 'bg-[#F77F11]', red: 'bg-red-400', gray: 'bg-gray-300' }[color] || 'bg-emerald-400';
  return (
    <span className={`relative inline-flex w-1.5 h-1.5 shrink-0 ${className}`} aria-hidden="true">
      <span className={`absolute inset-0 rounded-full ${ping} animate-ping opacity-60`} />
      <span className={`relative w-1.5 h-1.5 rounded-full ${solid}`} />
    </span>
  );
}

export function Stat({ label, value, sub, testid, accent, help, isEs, scope, source, tone }) {
  const { label: rangeLabel } = useRangeLabel(isEs);
  const bar = tone || (accent
    ? 'linear-gradient(90deg,#C2570A,#F77F11)'
    : 'linear-gradient(90deg,rgba(247,127,17,0.55),rgba(247,127,17,0.06))');
  return (
    <div className="relative bg-white border border-gray-200 rounded-2xl p-5 os3-rise transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(15,18,25,0.08)]" data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl" style={{ background: bar }} aria-hidden="true" />
      <p className="text-xs tracking-wider uppercase text-gray-500 font-medium min-h-[32px] flex items-start gap-1.5">
        <span className="inline-flex items-center">{label}{help && <InfoTip k={help} isEs={isEs} />}</span>
      </p>
      <div className="min-h-[20px] flex items-center gap-1.5">
        {scope === 'current' && <ScopeTag current isEs={isEs} />}
        {scope === 'period' && <ScopeTag label={rangeLabel} isEs={isEs} />}
        {scope === 'lifetime' && <ScopeTag label={isEs ? 'Histórico' : 'Lifetime'} isEs={isEs} />}
      </div>
      <p className={`mt-1.5 text-2xl lg:text-[28px] font-bold tracking-tight tabular-nums ${accent ? 'text-[#F77F11]' : 'text-gray-900'}`}>
        {typeof value === 'number' ? <AnimatedNumber value={value} /> : value}
      </p>
      {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
      {source && (
        <p className="mt-1 inline-flex items-center gap-1 text-[10px] text-gray-400" data-testid={testid ? `${testid}-source` : undefined}>
          <Database className="w-2.5 h-2.5" aria-hidden="true" /> {isEs ? 'Fuente' : 'Source'}: {source}
        </p>
      )}
    </div>
  );
}

export function SourceNote({ source, isEs, className = '' }) {
  if (!source) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] text-gray-400 ${className}`}>
      <Database className="w-2.5 h-2.5" aria-hidden="true" /> {isEs ? 'Fuente' : 'Source'}: {source}
    </span>
  );
}

export function LoadingState() {
  return (
    <div className="space-y-5" data-testid="os2-loading" aria-busy="true">
      <div className="space-y-2">
        <div className="os3-skeleton h-7 w-64 max-w-full" />
        <div className="os3-skeleton h-3.5 w-96 max-w-full" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl border p-4" style={{ borderColor: 'var(--os-border-soft)', background: 'var(--os-surface)' }}>
            <div className="os3-skeleton h-3 w-20" />
            <div className="os3-skeleton h-8 w-28 mt-3" />
            <div className="os3-skeleton h-3 w-24 mt-2.5" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[0, 1].map((k) => (
          <div key={k} className="rounded-2xl border p-5" style={{ borderColor: 'var(--os-border-soft)', background: 'var(--os-surface)' }}>
            <div className="os3-skeleton h-3.5 w-32" />
            <div className="mt-5 flex items-end gap-2.5 h-36">
              {[40, 70, 55, 90, 62, 78, 48, 84].map((h, i) => (
                <div key={i} className="os3-skeleton flex-1" style={{ height: `${h}%`, borderRadius: '8px 8px 4px 4px' }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EmptyList({ isEs, text }) {
  return <p className="text-[13px] text-gray-400 py-2" data-testid="os2-empty-list">{text || (isEs ? 'Sin datos en este período' : 'No data in this period')}</p>;
}

export function NotConnected({ label, isEs, testid, help }) {
  return (
    <Card className="p-5 border-dashed" data-testid={testid}>
      <p className="text-xs tracking-wider uppercase text-gray-400 font-medium flex items-center">
        {label}{help && <InfoTip k={help} isEs={isEs} />}
      </p>
      <p className="mt-2 text-lg font-medium text-gray-300">—</p>
      <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400">
        <Database className="w-3 h-3" aria-hidden="true" />
        {isEs ? 'Fuente de datos no conectada aún' : 'Data source not connected yet'}
      </p>
    </Card>
  );
}

export function EmptyModule({ icon: Icon, title, question, note, isEs, children }) {
  return (
    <div>
      <PageHead title={title} question={question} />
      {children}
      <Card className="border-dashed p-12 flex flex-col items-center text-center" data-testid="os2-module-empty">
        {Icon && <Icon className="w-8 h-8 text-gray-300 mb-4" strokeWidth={1.5} aria-hidden="true" />}
        <p className="text-sm font-medium text-gray-500">{note || (isEs ? 'Fuente de datos no conectada aún' : 'Data source not connected yet')}</p>
        <p className="mt-1 text-xs text-gray-400">{isEs ? 'Este módulo se activa en la siguiente fase — la estructura ya está lista.' : 'This module activates in the next phase — the structure is ready.'}</p>
      </Card>
    </div>
  );
}

const STAGE_STYLE = {
  NEW_LEAD: 'bg-blue-50 text-blue-700 border-blue-200',
  ENGAGED: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  QUALIFIED: 'bg-amber-50 text-amber-700 border-amber-200',
  CALL_BOOKED: 'bg-violet-50 text-violet-700 border-violet-200',
  CUSTOMER: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const STAGE_LABEL = {
  en: { NEW_LEAD: 'New lead', ENGAGED: 'Engaged', QUALIFIED: 'Qualified', CALL_BOOKED: 'Call booked', CUSTOMER: 'Customer' },
  es: { NEW_LEAD: 'Lead nuevo', ENGAGED: 'Interesado', QUALIFIED: 'Calificado', CALL_BOOKED: 'Llamada agendada', CUSTOMER: 'Cliente' },
};

export function StageBadge({ stage, isEs }) {
  const cls = STAGE_STYLE[stage] || 'bg-gray-50 text-gray-600 border-gray-200';
  const label = STAGE_LABEL[isEs ? 'es' : 'en'][stage] || stage || '—';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${cls}`}>{label}</span>;
}

export function Dot({ ok }) {
  return ok
    ? <span className="inline-flex w-4 h-4 rounded-full bg-emerald-50 border border-emerald-200 items-center justify-center text-emerald-600 text-[9px] font-bold">✓</span>
    : <span className="inline-flex w-4 h-4 rounded-full bg-gray-50 border border-gray-200" aria-label="no" />;
}

export function Th({ children, className = '', help, isEs }) {
  const t = help ? helpText(help, isEs) : '';
  return (
    <th title={t || undefined}
      className={`px-3 py-2.5 text-left text-[11px] tracking-wider uppercase font-medium text-gray-500 whitespace-nowrap bg-gray-50/80 border-b border-gray-200 ${t ? 'cursor-help underline decoration-dotted decoration-gray-300 underline-offset-2' : ''} ${className}`}>
      {children}
    </th>
  );
}

export function Td({ children, className = '' }) {
  return <td className={`px-3 py-2.5 text-[13px] text-gray-700 whitespace-nowrap border-b border-gray-100 ${className}`}>{children}</td>;
}
